import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { computeStoplicht, stoplichtBg, formatDaysSilence } from '../../lib/journeyHelpers';
import type { UserJourneyStatus } from '../../types/klanttraject';
import { Pause, Play, Mail } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

interface Props {
  userId: string;
  journeyStatus: UserJourneyStatus;
  lastActivityAt: string | null;
  hasFailedOrPendingManual: boolean;
  onPersonalMailClick: () => void;
  onMutated: () => void;
}

export function JourneyStatusStrip({ userId, journeyStatus: propStatus, lastActivityAt: propLastActivity, hasFailedOrPendingManual, onPersonalMailClick, onMutated }: Props) {
  const { showToast } = useToast();
  const auth = useAuth();
  const [busy, setBusy] = useState(false);
  const [testBusy, setTestBusy] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [liveStatus, setLiveStatus] = useState<UserJourneyStatus>(propStatus);
  const [liveLastActivity, setLiveLastActivity] = useState<string | null>(propLastActivity);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('users').select('journey_status, journey_last_activity_at').eq('id', userId).maybeSingle();
      if (cancelled || !data) return;
      setLiveStatus((data.journey_status ?? 'inactive') as UserJourneyStatus);
      setLiveLastActivity(data.journey_last_activity_at ?? null);
    })();
    return () => { cancelled = true; };
  }, [userId, refreshTick, propStatus, propLastActivity]);

  const journeyStatus = liveStatus;
  const lastActivityAt = liveLastActivity;
  const stoplicht = computeStoplicht(journeyStatus, lastActivityAt, hasFailedOrPendingManual);

  const label = (() => {
    if (journeyStatus === 'completed') return 'Traject voltooid';
    if (journeyStatus === 'paused') return 'Gepauzeerd';
    if (journeyStatus === 'inactive') return 'Niet actief';
    if (journeyStatus === 'opted_out') return 'Uitgeschreven';
    if (!lastActivityAt) return 'Loopt';
    return formatDaysSilence(lastActivityAt);
  })();

  async function setStatus(newStatus: 'active' | 'paused') {
    setBusy(true);
    try {
      const { error } = await supabase.from('users').update({ journey_status: newStatus }).eq('id', userId);
      if (error) throw error;
      showToast(newStatus === 'paused' ? 'Traject gepauzeerd' : 'Traject hervat', 'success');
      setRefreshTick(t => t + 1);
      onMutated();
    } catch (e: any) {
      showToast(e.message || 'Fout bij wijzigen status', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function resetJourney() {
    if (!confirm('Reset het hele klanttraject voor deze user? Alle progress wordt gewist (bron-data zoals blogs blijft staan).')) return;
    setTestBusy('reset');
    try {
      const { error } = await supabase.rpc('reset_user_journey', { p_user_id: userId });
      if (error) throw error;
      showToast('Traject gereset', 'success');
      setRefreshTick(t => t + 1);
      onMutated();
    } catch (e: any) {
      showToast('Reset mislukt', 'error', e.message);
    } finally { setTestBusy(null); }
  }

  async function reseedJourney() {
    setTestBusy('reseed');
    try {
      const { error } = await supabase.rpc('reseed_user_journey', { p_user_id: userId });
      if (error) throw error;
      showToast('Traject opnieuw gestart', 'success');
      setRefreshTick(t => t + 1);
      onMutated();
    } catch (e: any) {
      showToast('Re-seed mislukt', 'error', e.message);
    } finally { setTestBusy(null); }
  }

  async function fastForwardJourney() {
    if (!confirm('Versnel de hele keten? Alle resterende stappen worden direct verwerkt (mails worden verstuurd).')) return;
    setTestBusy('forward');
    try {
      const { error } = await supabase.rpc('fast_forward_user_journey', { p_user_id: userId });
      if (error) throw error;
      // Trigger journey-tick a few times so the chain processes
      const { data: { session } } = await supabase.auth.getSession();
      for (let i = 0; i < 8; i++) {
        const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/journey-tick`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${session?.access_token ?? ''}` },
        });
        const result = await resp.json();
        if (!result.processed || result.processed === 0) break;
        // small delay so DB writes settle between iterations
        await new Promise(r => setTimeout(r, 400));
      }
      showToast('Keten versneld', 'success');
      setRefreshTick(t => t + 1);
      onMutated();
    } catch (e: any) {
      showToast('Versnellen mislukt', 'error', e.message);
    } finally { setTestBusy(null); }
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className={`inline-block w-3 h-3 rounded-full ${stoplichtBg(stoplicht)}`} />
          <div>
            <div className="text-sm font-medium text-slate-900">Klanttraject — {label}</div>
          </div>
        </div>
        <div className="flex gap-2">
          {journeyStatus === 'active' ? (
            <button onClick={() => setStatus('paused')} disabled={busy} className="px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md flex items-center gap-1 disabled:opacity-50">
              <Pause className="w-4 h-4" /> Pauzeer
            </button>
          ) : journeyStatus === 'paused' ? (
            <button onClick={() => setStatus('active')} disabled={busy} className="px-3 py-1.5 text-sm bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded-md flex items-center gap-1 disabled:opacity-50">
              <Play className="w-4 h-4" /> Hervat
            </button>
          ) : null}
          <button onClick={onPersonalMailClick} className="px-3 py-1.5 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-md flex items-center gap-1">
            <Mail className="w-4 h-4" /> Stuur persoonlijke mail
          </button>
        </div>
      </div>
      {auth.isAdmin && (
        <div className="border-t border-slate-100 mt-3 pt-3 flex items-center gap-2 text-xs">
          <span className="text-slate-500 mr-1">Test-acties:</span>
          <button onClick={reseedJourney} disabled={testBusy !== null} className="px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded text-slate-700 disabled:opacity-50">
            {testBusy === 'reseed' ? 'Bezig...' : '↻ Reset & re-seed'}
          </button>
          <button onClick={fastForwardJourney} disabled={testBusy !== null} className="px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded text-slate-700 disabled:opacity-50">
            {testBusy === 'forward' ? 'Bezig...' : '⏩ Versnel keten'}
          </button>
          <button onClick={resetJourney} disabled={testBusy !== null} className="px-2 py-1 bg-red-50 hover:bg-red-100 text-red-700 rounded disabled:opacity-50">
            {testBusy === 'reset' ? 'Bezig...' : '🗑 Reset (zonder re-seed)'}
          </button>
        </div>
      )}
    </div>
  );
}
